import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Session } from '@/types/auth';

interface SessionState {
  session: Session | null;
  setSession: (s: Session | null) => void;
  signOut: () => void;
}

/**
 * Persists in localStorage under `kiwasco_session` — the same key the axios
 * interceptor reads. Keeping them aligned means signing out anywhere logs
 * the user out of all in-flight requests too.
 */
export const useSession = create<SessionState>()(
  persist(
    (set) => ({
      session: null,
      setSession: (session) => set({ session }),
      signOut: () => {
        set({ session: null });
        // Belt-and-braces: also nuke the key directly in case the interceptor
        // reads before the persist middleware has flushed.
        localStorage.removeItem('kiwasco_session');
      },
    }),
    {
      name: 'kiwasco_session',
      storage: createJSONStorage(() => localStorage),
      // Only persist the session field — not the actions.
      partialize: (state) => ({ session: state.session }),
    },
  ),
);

/** Tiny convenience selector for the auth-check guard. */
export function useIsAuthenticated() {
  return useSession((s) => Boolean(s.session?.auth?.token));
}

/** Returns the current user (or null). */
export function useCurrentUser() {
  return useSession((s) => s.session?.user ?? null);
}
